// growth-slider
import { onDestroy, onMount } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { Resize } from "@lib/subs";

const AUTOPLAY_SECONDS = 10;
const SLIDE_DURATION = 0.9;
const SLIDE_EASE = "expo.out";
const DOT_DURATION = 0.55;
// power3.inOut: symmetric ease keeps total dot widths constant per frame, preventing row reflow
const DOT_EASE = "power3.inOut";
const ACTIVE_CLASS = "is--active";
const SMALL_DOT_FALLBACK = 8;
const DRAG_THRESHOLD = 6; // px of travel before a press becomes a drag
const DRAG_COMMIT = 0.18; // fraction of track width that commits a slide change
const DRAG_FLING_VEL = 0.4; // px/ms release speed that commits regardless of distance
const DRAG_RESIST = 0.35; // rubber-band resistance dragging past the first/last slide

export default function (element: HTMLElement) {
  const slides = Array.from(
    element.querySelectorAll<HTMLElement>('[growth-slider="item"]'),
  );
  const controlsInner = element.querySelector<HTMLElement>(
    '[growth-slider="controls-inner"]',
  );
  const dotTemplate = element.querySelector<HTMLElement>(
    '[growth-slider="dots-m"]',
  );
  const prevBtn = element.querySelector<HTMLElement>(
    '[growth-slider="controls-prev"]',
  );
  const nextBtn = element.querySelector<HTMLElement>(
    '[growth-slider="controls-next"]',
  );

  if (slides.length === 0) return;

  const count = slides.length;
  let current = 0;
  let progress: gsap.core.Tween | null = null;
  let paused = false;
  let track: HTMLElement | null = null;
  let dotsTl: gsap.core.Timeline | null = null;
  let smallDot = SMALL_DOT_FALLBACK;
  let expandedDot = 0;

  let dragging = false;
  let dragPending = false;
  let dragPointerId = -1;
  let dragStartX = 0;
  let dragStartY = 0;
  let trackWidth = 1;
  let dragLastX = 0;
  let dragLastT = 0;
  let dragVel = 0;
  let suppressClick = false;

  // clip-path inset instead of overflow:hidden so vertical overflow (e.g. timeline labels) isn't clipped
  function buildTrack() {
    const anchor = slides[0];
    if (!anchor || !anchor.parentNode) return;

    track = document.createElement("div");
    track.setAttribute("growth-slider", "track");
    Object.assign(track.style, {
      position: "relative",
      display: "flex",
      width: "100%",
      height: "100%",
      overflow: "visible",
      clipPath: "inset(-100% 0px -100% 0px)",
    });

    anchor.parentNode.insertBefore(track, anchor);
    slides.forEach((slide) => track!.appendChild(slide));
  }

  const dots: HTMLElement[] = [];
  const fills: HTMLElement[] = [];

  function buildDots() {
    if (!controlsInner || !dotTemplate) return;

    for (let i = 0; i < count; i++) {
      const dot =
        i === 0 ? dotTemplate : (dotTemplate.cloneNode(true) as HTMLElement);

      const fill = dot.querySelector<HTMLElement>('[growth-slider="inner"]');
      if (!fill) continue;

      dot.classList.remove(ACTIVE_CLASS);
      gsap.set(dot, { flexGrow: 0, flexShrink: 0 });

      // is--active stays on permanently; visibility is controlled by scaleX, not bg
      fill.classList.add(ACTIVE_CLASS);
      gsap.set(fill, {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        transformOrigin: "0% 50%",
        scaleX: 0,
      });

      dot.setAttribute("role", "button");
      dot.setAttribute("aria-label", `Show slide ${i + 1} of ${count}`);
      dot.setAttribute("tabindex", i === 0 ? "0" : "-1");
      dot.style.cursor = "pointer";

      if (i !== 0) controlsInner.appendChild(dot);

      dots.push(dot);
      fills.push(fill);
    }
  }

  function computeDotSizes() {
    if (!controlsInner || dots.length === 0) return;

    const probe = dots[0]!;
    const minW = parseFloat(getComputedStyle(probe).minWidth);
    smallDot = Number.isFinite(minW) && minW > 0 ? minW : SMALL_DOT_FALLBACK;

    const gap = parseFloat(getComputedStyle(controlsInner).columnGap) || 0;
    const rowW = controlsInner.clientWidth;
    expandedDot = Math.max(smallDot, rowW - (count - 1) * (smallDot + gap));
  }

  function updateDots(animate: boolean) {
    dotsTl?.kill();
    const tl = animate && !reduced ? gsap.timeline() : null;
    dotsTl = tl;

    dots.forEach((dot, i) => {
      const active = i === current;
      const width = active ? expandedDot : smallDot;

      // snap color via class — RGB tween muddies near-black ↔ cream
      dot.classList.toggle(ACTIVE_CLASS, active);

      if (tl) {
        tl.to(dot, { width, duration: DOT_DURATION, ease: DOT_EASE }, 0);
      } else {
        gsap.set(dot, { width });
      }

      dot.setAttribute("tabindex", active ? "0" : "-1");
      if (active) dot.setAttribute("aria-current", "true");
      else dot.removeAttribute("aria-current");

      if (!active) gsap.set(fills[i]!, { scaleX: 0 });
    });
  }

  function setupA11y() {
    element.setAttribute("role", "group");
    element.setAttribute("aria-roledescription", "carousel");
    if (!element.getAttribute("aria-label")) {
      element.setAttribute("aria-label", "Growth slider");
    }

    slides.forEach((slide, i) => {
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "slide");
      slide.setAttribute("aria-label", `${i + 1} of ${count}`);
      slide.setAttribute("aria-hidden", String(i !== 0));
    });

    controlsInner?.setAttribute("role", "group");
    controlsInner?.setAttribute("aria-label", "Choose slide to display");
  }

  function startProgress() {
    progress?.kill();
    const fill = fills[current];
    if (!fill) return;

    if (reduced || count <= 1) {
      gsap.set(fill, { scaleX: 1 });
      return;
    }

    progress = gsap.fromTo(
      fill,
      { scaleX: 0 },
      {
        scaleX: 1,
        duration: AUTOPLAY_SECONDS,
        ease: "none",
        paused,
        onComplete: () => goTo(current + 1),
      },
    );
  }

  function render(animate: boolean) {
    const x = -100 * current;
    if (animate && !reduced) {
      gsap.to(slides, {
        xPercent: x,
        duration: SLIDE_DURATION,
        ease: SLIDE_EASE,
        overwrite: "auto",
      });
    } else {
      gsap.set(slides, { xPercent: x });
    }

    slides.forEach((slide, i) =>
      slide.setAttribute("aria-hidden", String(i !== current)),
    );
  }

  function goTo(index: number) {
    const next = ((index % count) + count) % count;
    current = next;
    render(true);
    updateDots(true);
    startProgress();
  }

  function pause() {
    if (paused) return;
    paused = true;
    progress?.pause();
  }

  function resume() {
    if (!paused) return;
    paused = false;
    progress?.resume();
  }

  function onDotClick(i: number) {
    return () => {
      dots[i]?.focus();
      goTo(i);
    };
  }

  function onKeydown(e: KeyboardEvent) {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        goTo(current + 1);
        dots[current]?.focus();
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        goTo(current - 1);
        dots[current]?.focus();
        break;
      case "Home":
        e.preventDefault();
        goTo(0);
        dots[current]?.focus();
        break;
      case "End":
        e.preventDefault();
        goTo(count - 1);
        dots[current]?.focus();
        break;
    }
  }

  function onDotKey(i: number) {
    return (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        goTo(i);
      }
    };
  }

  function setupArrow(
    el: HTMLElement | null,
    dir: number,
    label: string,
  ): (() => void) | null {
    if (!el) return null;
    el.setAttribute("role", "button");
    el.setAttribute("aria-label", label);
    el.setAttribute("tabindex", "0");
    el.style.cursor = "pointer";

    const step = () => goTo(current + dir);
    const key = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        step();
      }
    };
    el.addEventListener("click", step);
    el.addEventListener("keydown", key);

    return () => {
      el.removeEventListener("click", step);
      el.removeEventListener("keydown", key);
      el.removeAttribute("role");
      el.removeAttribute("aria-label");
      el.removeAttribute("tabindex");
      el.style.cursor = "";
    };
  }

  function onDragDown(e: PointerEvent) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Let inner modules with their own pointer handling claim the gesture.
    if ((e.target as Element)?.closest?.("[data-no-drag]")) return;
    dragPending = true;
    dragging = false;
    dragPointerId = e.pointerId;
    dragStartX = dragLastX = e.clientX;
    dragStartY = e.clientY;
    dragLastT = performance.now();
    dragVel = 0;
    trackWidth = track?.clientWidth || element.clientWidth || 1;
    gsap.killTweensOf(slides);
    pause();
  }

  function onDragMove(e: PointerEvent) {
    if (e.pointerId !== dragPointerId) return;
    const dx = e.clientX - dragStartX;
    const dy = e.clientY - dragStartY;
    if (dragPending) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      dragPending = false;
      if (Math.abs(dx) <= Math.abs(dy)) return; // vertical → let the page scroll
      dragging = true;
      suppressClick = true;
      track && (track.style.cursor = "grabbing");
      try {
        track?.setPointerCapture(e.pointerId);
      } catch {
        /* not all pointers can be captured */
      }
    }
    if (!dragging) return;
    const now = performance.now();
    const dt = now - dragLastT;
    if (dt > 0) dragVel = (e.clientX - dragLastX) / dt;
    dragLastX = e.clientX;
    dragLastT = now;
    let pct = (dx / trackWidth) * 100;
    if ((current === 0 && dx > 0) || (current === count - 1 && dx < 0)) {
      pct *= DRAG_RESIST;
    }
    gsap.set(slides, { xPercent: -100 * current + pct });
  }

  function onDragUp(e: PointerEvent) {
    if (e.pointerId !== dragPointerId) return;
    if (track?.hasPointerCapture(e.pointerId)) {
      track.releasePointerCapture(e.pointerId);
    }
    if (track) track.style.cursor = "grab";
    if (!dragging) {
      dragPending = false;
      return;
    }
    dragging = false;
    dragPending = false;
    const frac = (e.clientX - dragStartX) / trackWidth;
    const fling = Math.abs(dragVel) > DRAG_FLING_VEL;
    let step = 0;
    if (frac <= -DRAG_COMMIT || (fling && dragVel < 0)) step = 1;
    else if (frac >= DRAG_COMMIT || (fling && dragVel > 0)) step = -1;
    const target = current + step;
    // Drag doesn't wrap — past either end just snaps back to the current slide.
    goTo(target < 0 || target > count - 1 ? current : target);
  }

  function onDragCancel(e: PointerEvent) {
    if (e.pointerId !== dragPointerId) return;
    if (track?.hasPointerCapture(e.pointerId)) {
      track.releasePointerCapture(e.pointerId);
    }
    if (track) track.style.cursor = "grab";
    const wasDragging = dragging;
    dragging = false;
    dragPending = false;
    if (wasDragging) goTo(current);
  }

  function onDragClick(e: MouseEvent) {
    if (!suppressClick) return;
    suppressClick = false;
    e.stopPropagation();
    e.preventDefault();
  }

  function onTrackTouchMove(e: TouchEvent) {
    if (dragging && e.cancelable) e.preventDefault();
  }

  function onVisibility() {
    if (document.hidden) pause();
    else resume();
  }

  onMount(() => {
    buildTrack();

    setupA11y();
    buildDots();
    computeDotSizes();
    render(false);
    updateDots(false);
    startProgress();

    const unsubResize = Resize.add(() => {
      computeDotSizes();
      updateDots(false);
    });

    const dotClicks = dots.map((dot, i) => {
      const click = onDotClick(i);
      const key = onDotKey(i);
      dot.addEventListener("click", click);
      dot.addEventListener("keydown", key);
      return { dot, click, key };
    });

    controlsInner?.addEventListener("keydown", onKeydown);

    const cleanupPrev = setupArrow(prevBtn, -1, "Previous slide");
    const cleanupNext = setupArrow(nextBtn, 1, "Next slide");

    if (track) {
      track.style.cursor = "grab";
      track.style.touchAction = "pan-y";
      track.addEventListener("pointerdown", onDragDown);
      track.addEventListener("pointermove", onDragMove);
      track.addEventListener("pointerup", onDragUp);
      track.addEventListener("pointercancel", onDragCancel);
      track.addEventListener("click", onDragClick, { capture: true });
      track.addEventListener("touchmove", onTrackTouchMove, { passive: false });
    }

    element.addEventListener("pointerenter", pause);
    element.addEventListener("pointerleave", resume);
    element.addEventListener("focusin", pause);
    element.addEventListener("focusout", resume);
    document.addEventListener("visibilitychange", onVisibility);

    onDestroy(() => {
      progress?.kill();
      progress = null;
      dotsTl?.kill();
      dotsTl = null;
      gsap.killTweensOf(slides);

      dotClicks.forEach(({ dot, click, key }) => {
        dot.removeEventListener("click", click);
        dot.removeEventListener("keydown", key);
      });
      controlsInner?.removeEventListener("keydown", onKeydown);
      cleanupPrev?.();
      cleanupNext?.();
      if (track) {
        track.removeEventListener("pointerdown", onDragDown);
        track.removeEventListener("pointermove", onDragMove);
        track.removeEventListener("pointerup", onDragUp);
        track.removeEventListener("pointercancel", onDragCancel);
        track.removeEventListener("click", onDragClick, { capture: true });
        track.removeEventListener("touchmove", onTrackTouchMove);
      }
      unsubResize();
      element.removeEventListener("pointerenter", pause);
      element.removeEventListener("pointerleave", resume);
      element.removeEventListener("focusin", pause);
      element.removeEventListener("focusout", resume);
      document.removeEventListener("visibilitychange", onVisibility);

      dots.slice(1).forEach((dot) => dot.remove());
      const template = dots[0];
      if (template) {
        gsap.set(template, { clearProps: "all" });
        template.classList.add(ACTIVE_CLASS);
        template.removeAttribute("tabindex");
        template.removeAttribute("aria-current");
        template.style.cursor = "";
      }
      if (fills[0]) {
        gsap.set(fills[0], { clearProps: "all" });
        fills[0].classList.add(ACTIVE_CLASS);
      }

      if (track && track.parentNode) {
        slides.forEach((slide) =>
          track!.parentNode!.insertBefore(slide, track),
        );
        track.remove();
        track = null;
      }
      gsap.set(slides, { clearProps: "all" });
    });
  });
}
